import { faBuildingCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { Button, Card, Col, Row, Tab, Table, Tabs } from 'react-bootstrap';
import { FaBuilding } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import Pagination from '../../Pagination';
import './CandidateDashboard.css';
import DashboardLayout from './DashboardLayout';
import GlobalCandidateSettings from './GlobalCandidateSettings';

const CandidatesCompanies = () => {
  const BASE_API_URL = process.env.REACT_APP_API_URL;
  const location = useLocation();
  const userName = location.state?.userName;
  const userId = location.state?.userId;

  const appliedCompany = location.state?.appliedCompany;
  const storedTabKey = localStorage.getItem('activeCompanyTabKey');
  const [key, setKey] = useState(appliedCompany ? 'appliedCompany' : (storedTabKey ? storedTabKey : 'all'));
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [sortedColumn, setSortedColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState(' ');
  const [isLoading, setIsLoading] = useState(false);

  // NEW STATE to hold last applied dates by companyName
  const [lastAppliedDates, setLastAppliedDates] = useState({});

  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem('activeCompanyTabKey', key);
  }, [key]);

  useEffect(() => {
    setPage(0);
  }, [key, search]);

  useEffect(() => {
    if (key === 'all') {
      fetchData();
    } else if (key === 'appliedCompany') {
      fetchDataByAppliedCompanies();
    }
  }, [key, search, page, pageSize, sortOrder, sortedColumn]);

  // Fetch companies for 'appliedCompany' tab
  const fetchDataByAppliedCompanies = async () => {
    setIsLoading(true);
    try {
      const url = search ? `${BASE_API_URL}/searchAppliedCompanies` : `${BASE_API_URL}/appliedCompanies`;
      const params = {
        search,
        userId,
        page,
        size: pageSize,
        sortBy: sortedColumn,
        sortOrder,
      };
      const appliedCompanies = await axios.get(url, { params });
      const filteredCompanies = appliedCompanies.data.content.filter(c => c !== null);
      if (filteredCompanies.length === 0 && search) {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Company not found!"
        });
        setSearch('');
      }
      setCompanies(filteredCompanies);
      setTotalPages(appliedCompanies.data.totalPages);
    } catch (error) {
      // console.log("Error fetching data: " + error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch companies for 'all' tab
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const url = search ? `${BASE_API_URL}/searchCompany` : `${BASE_API_URL}/companiesList`;
      const params = {
        search,
        page,
        size: pageSize,
        sortBy: sortedColumn,
        sortOrder,
      };
      const response = await axios.get(url, { params });
      const filteredCompanies = response.data.content.filter(c => c !== null);
      if (filteredCompanies.length === 0 && search) {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Company not found!"
        });
        setSearch('');
      }
      setCompanies(filteredCompanies);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      // console.log("Error fetching data: " + error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (column) => {
    let order = 'asc';
    if (sortedColumn === column) {
      order = sortOrder === 'asc' ? 'desc' : 'asc';
    }
    setSortedColumn(column);
    setSortOrder(order);
  };

  const handlePageClick = (data) => {
    const selectedPage = Math.max(0, Math.min(data.selected, totalPages - 1));
    setPage(selectedPage);
  };

  const handlePageSizeChange = (e) => {
    const size = parseInt(e.target.value);
    setPageSize(size);
    setPage(0);
  };

  const handleClick = (companyId) => {
    const company = companies.find((company) => company?.companyId === companyId);
    if (company) {
      const encodedCompanyName = encodeURIComponent(company.companyName);
      navigate(`/candidate-dashboard/companies/companyPage/companyName/${encodedCompanyName}`, {
        state: { companyId, userName, userId }
      });
    } else {
      console.error("Company not found!");
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  // NEW: Fetch last applied date for a company and store in state
  const fetchLastAppliedDate = async (companyName, userId) => {
    try {
      const response = await axios.get(`${BASE_API_URL}/lastApplicationDateByCompanyNameAndUserId`, {
        params: { companyName, userId }
      });
      // The response data is the raw date string (e.g. "2025-07-30T14:45:00.000+0000")
      const dateString = response.data;


      setLastAppliedDates(prev => ({
        ...prev,
        [companyName]: dateString
      }));
    } catch (error) {
      setLastAppliedDates(prev => ({
        ...prev,
        [companyName]: 'N/A'
      }));
    }
  };


  // NEW: Load last applied dates whenever companies list changes on 'appliedCompany' tab
  useEffect(() => {
    if (key === 'appliedCompany' && companies.length > 0) {
      companies.forEach(company => {
        if (company?.companyName && !lastAppliedDates[company.companyName]) {
          fetchLastAppliedDate(company.companyName, userId);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, key, userId]);

  const isLastPage = page === totalPages - 1;
  const isPageSizeDisabled = isLastPage;

  const customTabHeader = (title, icon) => (
    <div className="d-flex align-items-center">
      <span className="me-2">{icon}</span>
      <span className="fs-6 fw-bold">{title}</span>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="main-content">
        <Row className="d-flex justify-content-between align-items-center" style={{ marginBottom: '20px' }}>
          <Col xs="auto">
            <h2 className='text-start' style={{ paddingLeft: '12px', marginBottom: 0 }}>Companies</h2>
          </Col>
          <Col xs="auto">
            <GlobalCandidateSettings userId={userId} />
          </Col>
        </Row>

        <Row>
          <Col className='filter-action' style={{ paddingTop: '10px', marginLeft: '12px', marginRight: '12px' }}>
            <Tabs
              id="company-tabs"
              activeKey={key}
              onSelect={(k) => setKey(k)}
              className="mb-3"
            >
              <Tab eventKey="all" title={customTabHeader("All Companies", <FaBuilding />)} />
              <Tab eventKey="appliedCompany" title={customTabHeader("Applied Companies", <FontAwesomeIcon icon={faBuildingCircleCheck} />)} />
            </Tabs>
          </Col>
        </Row>

        <Row>
          <Col></Col>
          <Col md={3} className="d-flex align-items-right">
            <div className="search-bar" style={{ flex: 1 }}>
              <input
                style={{
                  borderColor: '#6c5b7b',
                  borderRadius: '6px',
                  height: '40px',
                  width: '100%',
                  padding: '0 10px',
                }}
                type="text"
                name="search"
                placeholder="Search"
                value={search}
                onChange={handleSearchChange}
              />
            </div>
          </Col>
        </Row>

        <br />

        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center">
            <div className="spinner-bubble spinner-bubble-primary m-5" />
            <span>Loading...</span>
          </div>
        ) : companies.length > 0 ? (
          <Card body>
            <div className="table-details-list table-wrapper">
              <Table hover responsive >
                <thead>
                  <tr>
                    <th scope="col" onClick={() => handleSort('companyName')} style={{ cursor: 'pointer' }}>
                      Company Name
                      <span>
                        <span style={{ color: sortedColumn === 'companyName' && sortOrder === 'asc' ? 'black' : 'gray' }}>↑</span>
                        <span style={{ color: sortedColumn === 'companyName' && sortOrder === 'desc' ? 'black' : 'gray' }}>↓</span>
                      </span>
                    </th>
                    <th scope="col" onClick={() => handleSort('industryService')} style={{ cursor: 'pointer' }}>
                      Industry
                      <span>
                        <span style={{ color: sortedColumn === 'industryService' && sortOrder === 'asc' ? 'black' : 'gray' }}>↑</span>
                        <span style={{ color: sortedColumn === 'industryService' && sortOrder === 'desc' ? 'black' : 'gray' }}>↓</span>
                      </span>
                    </th>
                    {key === 'appliedCompany' && (
                      <th>
                        Applied on
                      </th>
                    )}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((company) => {
                    if (!company) return null;
                    return (
                      <tr key={company.companyId}>
                        <td>{company.companyName}</td>
                        <td>
                          {company.companyStatus === "Approved"
                            ? (company.industryService?.trim() ? company.industryService : "Not specified")
                            : "Company is not registered yet"}
                        </td>
                        {key === 'appliedCompany' && (
                          // Render last applied date from state or show loading
                          <td>{lastAppliedDates[company.companyName] || 'Loading...'}</td>
                        )}
                        <td>
                          <Button variant="primary" onClick={() => handleClick(company.companyId)}>
                            View
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>

              </Table>
            </div>

            <Pagination
              page={page}
              pageSize={pageSize}
              totalPages={totalPages}
              handlePageSizeChange={handlePageSizeChange}
              isPageSizeDisabled={isPageSizeDisabled}
              handlePageClick={handlePageClick}
            />
          </Card>
        ) : (
          <div className="d-flex justify-content-center align-items-center">
            <span>No companies available</span>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CandidatesCompanies;
